import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { SadmEntityPickerOutput } from '@modules/sadm/components/sadm-entity-picker/sadm-entity-picker-output';
import { SadmEntityPickerComponent } from '@modules/sadm/components/sadm-entity-picker/sadm-entity-picker.component';
import { DEFAULT_SADM_ENTITY_NAME_KEY } from '@modules/sadm/utils/default-sadm-entity-name-key';
import { LoaderComponent } from '@modules/shared/loader/loader.component';
import { Media } from '@pixacare/domain';
import { SadmEntityDraft } from '@shared/models/draft/sadm-entity-draft';
import { TuiButton, TuiDialogContext } from '@taiga-ui/core';
import { POLYMORPHEUS_CONTEXT } from '@taiga-ui/polymorpheus';
import { combineLatest, first, Observable } from 'rxjs';
import {
  WoundtrackQuickModuleComponentInputs,
  WoundtrackQuickModuleComponentsOutputs,
} from '../../models/woundtrack-quick-module-component';
import { WoundtrackQuickService } from '../../services/woundtrack-quick.service';

@Component({
  selector: 'pxc-woundtrack-quick',
  imports: [CommonModule, TranslocoDirective, TuiButton, SadmEntityPickerComponent, LoaderComponent],
  templateUrl: './woundtrack-quick.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'h-full w-full py-6',
  },
  providers: [WoundtrackQuickService],
})
export class WoundtrackQuickComponent implements OnInit {
  private readonly context =
    inject<TuiDialogContext<WoundtrackQuickModuleComponentsOutputs, WoundtrackQuickModuleComponentInputs>>(
      POLYMORPHEUS_CONTEXT,
    );

  private readonly woundtrackQuickService = inject(WoundtrackQuickService);
  private readonly translocoService = inject(TranslocoService);

  readonly quickModule: WoundtrackQuickModuleComponentInputs['quickModule'] = this.context.data.quickModule;
  readonly medias: WoundtrackQuickModuleComponentInputs['medias'] = this.context.data.medias;

  readonly length: WoundtrackQuickModuleComponentInputs['length'] = this.context.data.length;
  readonly width: WoundtrackQuickModuleComponentInputs['width'] = this.context.data.width;
  readonly size: WoundtrackQuickModuleComponentInputs['size'] = this.context.data.size;
  readonly vertices: WoundtrackQuickModuleComponentInputs['vertices'] = this.context.data.vertices;
  readonly fibrin: WoundtrackQuickModuleComponentInputs['fibrin'] = this.context.data.fibrin;
  readonly granulation: WoundtrackQuickModuleComponentInputs['granulation'] = this.context.data.granulation;
  readonly necrosis: WoundtrackQuickModuleComponentInputs['necrosis'] = this.context.data.necrosis;
  readonly pictures: WoundtrackQuickModuleComponentInputs['pictures'] = this.context.data.pictures;
  readonly predictedLength: WoundtrackQuickModuleComponentInputs['predictedLength'] = this.context.data.predictedLength;
  readonly predictedWidth: WoundtrackQuickModuleComponentInputs['predictedWidth'] = this.context.data.predictedWidth;
  readonly predictedSize: WoundtrackQuickModuleComponentInputs['predictedSize'] = this.context.data.predictedSize;
  readonly predictedVertices: WoundtrackQuickModuleComponentInputs['predictedVertices'] =
    this.context.data.predictedVertices;
  readonly manualLength: WoundtrackQuickModuleComponentInputs['manualLength'] = this.context.data.manualLength;
  readonly manualWidth: WoundtrackQuickModuleComponentInputs['manualWidth'] = this.context.data.manualWidth;
  readonly manualSize: WoundtrackQuickModuleComponentInputs['manualSize'] = this.context.data.manualSize;
  readonly manualVertices: WoundtrackQuickModuleComponentInputs['manualVertices'] = this.context.data.manualVertices;
  readonly isManuallyMeasured: WoundtrackQuickModuleComponentInputs['isManuallyMeasured'] =
    this.context.data.isManuallyMeasured;

  sadmEntityPickerOutput: SadmEntityPickerOutput;
  isValid = false;
  sadmEntityDraft: SadmEntityDraft;

  get media(): Media {
    const [media] = this.medias;
    if (!media) {
      console.error('Media is required');
      this.context.completeWith(null);
      return null;
    }
    return media;
  }

  readonly clientCode$: Observable<string> = this.woundtrackQuickService.clientCode$;

  readonly patientId$: Observable<number> = this.woundtrackQuickService.selectPatientId$(this.media);

  ngOnInit(): void {
    if (!this.quickModule) {
      console.error('Quick Action is required');
      this.context.completeWith(null);
      return null;
    }
    combineLatest([this.clientCode$, this.patientId$])
      .pipe(first())
      .subscribe(([clientCode, patientId]) => {
        this.sadmEntityDraft = this.buildDefaultSadmEntity(clientCode, patientId, this.quickModule.sadmClientId);
      });
  }

  validate(): void {
    if (!this.sadmEntityPickerOutput) {
      return;
    }

    this.woundtrackQuickService.createAnalysis({
      media: this.media,
      quickModule: this.quickModule,
      pickerOutput: this.sadmEntityPickerOutput,
      moduleResults: {
        length: this.length,
        width: this.width,
        size: this.size,
        vertices: this.vertices,
        fibrin: this.fibrin,
        granulation: this.granulation,
        necrosis: this.necrosis,
        pictures: this.pictures,
        predictedLength: this.predictedLength,
        predictedWidth: this.predictedWidth,
        predictedSize: this.predictedSize,
        predictedVertices: this.predictedVertices,
        manualLength: this.manualLength,
        manualWidth: this.manualWidth,
        manualSize: this.manualSize,
        manualVertices: this.manualVertices,
        isManuallyMeasured: this.isManuallyMeasured,
      },
    });

    this.context.completeWith(this.sadmEntityPickerOutput);
  }

  close(): void {
    this.context.completeWith(null);
  }

  onSadmEntityCreated(sadmEntityDraft: SadmEntityDraft): void {
    this.onSadmEntityPickerSelectionChange({ sadmEntityDraft });
    this.validate();
  }

  onSadmEntityPickerSelectionChange(value: SadmEntityPickerOutput): void {
    this.sadmEntityPickerOutput = value;
    this.isValid = !!value;
  }

  private buildDefaultSadmEntity(clientCode: string, patientId: number, sadmClientId: number): SadmEntityDraft {
    return {
      name: this.translocoService.translate(DEFAULT_SADM_ENTITY_NAME_KEY),
      sadmClientId,
      clientCode,
      patientId,
      entityCreationFormAnswer: { answers: {} },
    };
  }
}
